import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { QuickAnalytic } from '../../types';

interface QuickAnalyticsProps {
  analytics: QuickAnalytic[];
}

const QuickAnalyticsCard: React.FC<{ analytic: QuickAnalytic; delay: number }> = ({
  analytic,
  delay,
}) => {
  const Icon = analytic.icon;
  const TrendIcon = analytic.changeType === 'positive' ? TrendingUp : TrendingDown;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="group"
    >
      <div className="flex flex-col justify-between rounded-3xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-900/5 transition-all duration-300 hover:shadow-2xl hover:shadow-gray-900/10">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-xs font-semibold text-gray-500">{analytic.title}</span>
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 shadow-sm">
            <Icon className="h-4 w-4" />
          </div>
        </div>

        <div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black text-gray-900">{analytic.value}</span>
            <span className="text-xs font-semibold text-gray-400">{analytic.unit}</span>
          </div>

          <div className="mt-2 flex items-center gap-1">
            <TrendIcon
              className={`h-3.5 w-3.5 ${
                analytic.changeType === 'positive' ? 'text-emerald-500' : 'text-red-500'
              }`}
            />
            <span
              className={`text-xs font-bold ${
                analytic.changeType === 'positive' ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {analytic.change}
            </span>
          </div>

          {analytic.action && (
            <button
              onClick={analytic.action.onClick}
              className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-700 transition hover:bg-gray-100"
            >
              {analytic.action.label}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const QuickAnalytics: React.FC<QuickAnalyticsProps> = ({ analytics }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {analytics.map((analytic, index) => (
        <QuickAnalyticsCard
          key={index}
          analytic={analytic}
          delay={0.08 * (index + 1)}
        />
      ))}
    </div>
  );
};

export default QuickAnalytics;